import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Eye, ArrowLeft } from 'lucide-react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useContent, BlogPost, DocPost } from '../../contexts/SupabaseContentContext';
import ImageUpload from '../../components/ImageUpload';
import { QuillImageUpload } from '../../components/QuillImageUpload';
import { replaceImagesInContent, replaceImageUrl } from '../../utils/imageReplacer';

// Register the image upload module with the Quill instance
Quill.register('modules/imageUpload', QuillImageUpload);

const AdminEditor: React.FC = () => {
  const { type, id } = useParams<{ type: 'blog' | 'doc'; id?: string }>();
  const navigate = useNavigate();
  const { 
    blogPosts, 
    docPosts, 
    addBlogPost, 
    updateBlogPost, 
    addDocPost, 
    updateDocPost,
    getBlogPost,
    getDocPost,
    getCategoriesByType
  } = useContent();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [published, setPublished] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [featuredImage, setFeaturedImage] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [order, setOrder] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [processingImages, setProcessingImages] = useState(false);

  const isEditing = !!id;
  const isBlog = type === 'blog';
  const categories = getCategoriesByType(type || 'blog');

  useEffect(() => {
    if (isEditing) {
      const post = isBlog ? getBlogPost(id) : getDocPost(id);
      if (post) {
        setTitle(post.title);
        setSlug(post.slug);
        setContent(post.content);
        setExcerpt(post.excerpt);
        setPublished(post.published);
        setCategoryId(post.category_id || '');
        
        if (isBlog && 'tags' in post) {
          setTags(post.tags);
          setFeaturedImage(post.featured_image);
        }
        
        if (!isBlog && 'order' in post) {
          setOrder(post.order);
        }
      }
    }
  }, [id, isEditing, isBlog, getBlogPost, getDocPost]);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    if (!isEditing) {
      setSlug(generateSlug(newTitle));
    }
  };

  const handleTagsChange = (tagsString: string) => {
    const tagArray = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);
    setTags(tagArray);
  };

  const handleImageUploaded = (url: string) => {
    setFeaturedImage(url);
  };

  const handleSave = async (publishNow = false) => {
    if (!title.trim() || !content.trim()) {
      alert('Vui lòng nhập tiêu đề và nội dung');
      return;
    }

    if (!categoryId) {
      alert('Vui lòng chọn danh mục');
      return;
    }

    setIsLoading(true);
    setProcessingImages(true);

    try {
      // Process content to replace images with Supabase Storage URLs
      const processedContent = await replaceImagesInContent(content);
      
      // Process featured image if needed
      let processedFeaturedImage = featuredImage;
      if (isBlog && featuredImage && !featuredImage.includes(import.meta.env.VITE_SUPABASE_URL)) {
        processedFeaturedImage = await replaceImageUrl(featuredImage);
      }

      const baseData = {
        title: title.trim(),
        slug: slug.trim() || generateSlug(title),
        content: processedContent,
        excerpt: excerpt.trim() || processedContent.replace(/<[^>]*>/g, '').substring(0, 200) + '...',
        author: 'Admin',
        published: publishNow || published,
        category_id: categoryId
      };

      if (isBlog) {
        const blogData = {
          ...baseData,
          tags,
          featured_image: processedFeaturedImage || 'https://images.pexels.com/photos/577585/pexels-photo-577585.jpeg'
        };

        if (isEditing) {
          updateBlogPost(id!, blogData);
        } else {
          addBlogPost(blogData);
        }
        navigate('/vmon-admin/blogs');
      } else {
        const docData = {
          ...baseData,
          order
        };

        if (isEditing) {
          updateDocPost(id!, docData);
        } else {
          addDocPost(docData);
        }
        navigate('/vmon-admin/docs');
      }
    } catch (error) {
      alert('Có lỗi xảy ra khi lưu. Vui lòng thử lại.');
    } finally {
      setProcessingImages(false);
      setIsLoading(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ],
    imageUpload: true
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate(isBlog ? '/vmon-admin/blogs' : '/vmon-admin/docs')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {isEditing ? 'Chỉnh sửa' : 'Tạo mới'} {isBlog ? 'bài viết' : 'tài liệu'}
                </h1>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => handleSave(false)}
                disabled={isLoading}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors inline-flex items-center disabled:opacity-50"
              >
                <Save className="h-5 w-5 mr-2" />
                Lưu nháp
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors inline-flex items-center disabled:opacity-50"
              >
                <Eye className="h-5 w-5 mr-2" />
                Xuất bản
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {processingImages && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                <p className="text-lg">Đang xử lý hình ảnh...</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tiêu đề
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="Nhập tiêu đề..."
              />
            </div>

            {/* Slug */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug (URL)
              </label>
              <div className="flex items-center">
                <span className="text-gray-500 mr-2">{isBlog ? 'https://vmon.vn/' : 'https://vmon.vn/docs/'}</span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  placeholder="url-slug"
                />
              </div>
            </div>

            {/* Content */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nội dung
              </label>
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  💡 <strong>Tip:</strong> Bạn có thể upload ảnh trực tiếp bằng cách nhấn vào nút ảnh trong toolbar. 
                  Ảnh sẽ được tự động upload lên Supabase Storage.
                </p>
              </div>
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={modules}
                style={{ height: '400px', marginBottom: '50px' }}
              />
            </div>

            {/* Excerpt */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tóm tắt
              </label>
              <textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="Tóm tắt ngắn gọn về nội dung..."
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publish Status */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Trạng thái</h3>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={published}
                  onChange={(e) => setPublished(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Xuất bản ngay</span>
              </label>
            </div>

            {/* Category */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Danh mục
              </label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Chọn danh mục</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Blog-specific fields */}
            {isBlog && (
              <>
                {/* Featured Image Upload */}
                <div className="bg-white rounded-lg shadow p-6">
                  <ImageUpload
                    onImageUploaded={handleImageUploaded}
                    currentImage={featuredImage}
                  />
                </div>

                {/* Tags */}
                <div className="bg-white rounded-lg shadow p-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (phân cách bằng dấu phẩy)
                  </label>
                  <input
                    type="text"
                    value={tags.join(', ')}
                    onChange={(e) => handleTagsChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    placeholder="VPS, Hosting, Linux"
                  />
                </div>
              </>
            )}

            {/* Doc-specific fields */}
            {!isBlog && (
              <>
                {/* Order */}
                <div className="bg-white rounded-lg shadow p-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thứ tự
                  </label>
                  <input
                    type="number"
                    value={order}
                    onChange={(e) => setOrder(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    min="1"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEditor;